import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { Box } from '@mui/system';
import { FORM_ERROR } from 'final-form';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import ConfirmationDialog from '@components/ConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import {
  getChannelHashes,
  addChannelHashes,
  updateChannelHashes,
  deleteChannelHashes,
  invalidateCache,
} from '../store/channel-hashes';
import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../components/TableToolbar';
import { viewChannelHashes } from '../payment-configuration.breadcrumbs';

function ChannelHashesToolbar({
  setNewChannelHashDialogOpen,
  refreshCache,
  fetchFromDB,
  setFetchFromDB,
  permissions = {},
}) {
  return (
    <TableToolbar>
      <div>
        <FormControlLabel
          control={
            <Switch
              checked={fetchFromDB}
              onChange={() => setFetchFromDB(!fetchFromDB)}
            />
          }
          label="Fetch from DB"
        />
        <Button
          variant="outlined"
          disabled={!permissions?.canRefreshChannelHashesCache}
          onClick={() => refreshCache()}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Refresh Cache
        </Button>

        <Button
          variant="contained"
          disabled={!permissions?.canCreateChannelHashes}
          onClick={() => setNewChannelHashDialogOpen(true)}
          startIcon={<AddIcon />}>
          Add new hash
        </Button>
      </div>
    </TableToolbar>
  );
}

export default function ChannelHashesList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const configList = useSelector((state) => state.channelHash.list);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [fetchFromDB, setFetchFromDB] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  const [toDeleteChannelHash, setToDeleteChannelHash] = useState('');
  const [newChannelHashDialogOpen, setNewChannelHashDialogOpen] =
    useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!permissions?.canViewChannelHashes) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewChannelHashes));
  }, [dispatch]);

  const deleteHandler = (id) => {
    setToDeleteChannelHash(id);
    setShowDeleteModal({ show: true });
  };

  const yesDeleteHandler = (id) => {
    dispatch(deleteChannelHashes({ id })).then((response) => {
      const { payload } = response;
      if (payload && payload.status === 'OK') {
        dispatch(showSuccessMessage('Channel hash deleted successfully'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const refreshCache = () => {
    dispatch(invalidateCache()).then(({ payload }) => {
      if (payload && payload.status === 'OK') {
        dispatch(showSuccessMessage('Successfully triggered refresh'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const resetForm = useCallback(() => {
    setNewChannelHashDialogOpen(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getChannelHashes(fetchFromDB)).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch, fetchFromDB]);

  const createNewChannelHash = (values) => {
    const { featureName, value } = values;
    const payload = isEditMode
      ? {
          featureName,
          value,
        }
      : values;

    return dispatch(
      isEditMode
        ? updateChannelHashes({
            payload,
            id: values.id,
          })
        : addChannelHashes({
            payload,
          }),
    ).then(({ payload: resp }) => {
      if (
        resp &&
        resp.data &&
        (resp.status === 'Created' || resp.status === 'OK')
      ) {
        dispatch(
          showSuccessMessage(
            `Channel hash ${isEditMode ? 'updated' : 'added'} successfully`,
          ),
        );
        return resetForm();
      }

      return { [FORM_ERROR]: resp.message };
    });
  };

  const validateNewChannelHash = (values) => {
    const errors = {};
    if (!values.orderType) {
      errors.orderType = 'Order type is mandatory and cannot be empty';
    }
    if (!values.platform) {
      errors.platform = 'Platform is mandatory and cannot be empty';
    }
    return errors;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Channel Hashes"
      pageReady>
      <DataGrid
        density="compact"
        rows={configList}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableSelectionOnClick
        loading={isFetchingData}
        components={{
          Toolbar: () =>
            ChannelHashesToolbar({
              setNewChannelHashDialogOpen,
              refreshCache,
              fetchFromDB,
              setFetchFromDB,
              permissions,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          setIsEditMode(true);
          setFormValues(row);
          setNewChannelHashDialogOpen(true);
        }}
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            width: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'orderType',
            headerName: 'Order Type',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            field: 'platform',
            headerName: 'Platform',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            flex: 1,
          },
          {
            field: 'channelHash',
            headerName: 'Channel Hash',
            sortable: false,
            headerAlign: 'center',
            align: 'left',
            flex: 3,
          },
          {
            field: 'actions',
            type: 'actions',
            headerName: 'Delete',
            getActions: ({ row }) => [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                disabled={!permissions?.canDeleteChannelHashes}
                key={row.id}
                onClick={() => deleteHandler(row.id)}
              />,
            ],
          },
        ]}
      />
      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={yesDeleteHandler}
        data={toDeleteChannelHash}
        title="Delete channel hash"
        message="Are you sure you want to delete the selected channel hash?"
      />
      <Dialog open={newChannelHashDialogOpen}>
        <DialogTitle>
          {isEditMode ? 'Update Existing Channel Hash' : 'Add New Channel Hash'}
        </DialogTitle>
        <Form
          onSubmit={createNewChannelHash}
          initialValues={isEditMode ? formValues : {}}
          validate={validateNewChannelHash}
          render={({ submitError, handleSubmit, values, submitting }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                {/* <DialogContentText>
                  Insert the config name that can be referenced from the
                  application level
                </DialogContentText> */}
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Order Type"
                    name="orderType"
                    required
                    variant="filled"
                    fullWidth
                    disabled={isEditMode}
                  />
                  <TextField
                    label="Platform"
                    name="platform"
                    required
                    InputProps={{
                      readOnly: !permissions?.canUpdateChannelHashes,
                    }}
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 20 }}
                  />
                  {isEditMode && (
                    <TextField
                      label="Channel Hash"
                      name="channelHash"
                      required
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="filled"
                      fullWidth
                      style={{ marginTop: 20 }}
                    />
                  )}
                </Box>
                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 20 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    !(
                      permissions?.canCreateChannelHashes ||
                      permissions?.canUpdateChannelHashes
                    ) ||
                    submitting ||
                    (isEditMode && values.platform === formValues.platform)
                  }>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
